import React, { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { supabase } from './lib/supabase'
import './index.css'

import Landing from './pages/Landing'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Dashboard from './pages/Dashboard'
import DailyRiddle from './pages/apps/DailyRiddle'
import AIRoast from './pages/apps/AIRoast'
import DarkHistory from './pages/apps/DarkHistory'
import VillainOrigin from './pages/apps/VillainOrigin'
import RoguesGallery from './pages/apps/RoguesGallery'
import CursorReticle from './components/CursorReticle'

// Simple boot splash used by SubscribedRoute while it fetches is_subscribed.
function BootSplash() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#030006'
    }}>
      <svg width="60" height="60" viewBox="0 0 160 160">
        <defs>
          <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff0055"/>
            <stop offset="100%" stopColor="#9000e0"/>
          </linearGradient>
        </defs>
        <rect width="160" height="160" rx="40" fill="#030006"/>
        <text x="80" y="118" textAnchor="middle" fontFamily="Bebas Neue" fontSize="118" fill="url(#g)" letterSpacing="-4">2</text>
      </svg>
    </div>
  )
}

// Gate for /app/* routes.
//   no session    -> /login
//   loading       -> BootSplash
//   not subscribed-> /dashboard (paywall lives there)
//   subscribed    -> render children
// Fail-closed: any error (RLS denial, missing row, network) routes to /dashboard.
function SubscribedRoute({ session, children }) {
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    let cancelled = false
    if (!session?.user) {
      setStatus('anonymous')
      return
    }
    setStatus('loading')
    supabase
      .from('members')
      .select('is_subscribed')
      .eq('user_id', session.user.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return
        if (error) {
          setStatus('denied')
          return
        }
        setStatus(data?.is_subscribed ? 'allowed' : 'denied')
      })
    return () => { cancelled = true }
  }, [session])

  if (status === 'loading') return <BootSplash />
  if (status === 'anonymous') return <Navigate to="/login" replace />
  if (status === 'denied') return <Navigate to="/dashboard" replace />
  return children
}

function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })

    // Failsafe: never stay stuck on loading screen longer than 5s
    const loadingTimeout = setTimeout(() => setLoading(false), 5000)

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
    })

    return () => {
      subscription.unsubscribe()
      clearTimeout(loadingTimeout)
    }
  }, [])

  if (loading) return (
    <div style={{ background: '#000', minHeight: '100vh', fontFamily: "'Share Tech Mono', monospace", padding: '40px 24px', position: 'relative', overflow: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Bebas+Neue&display=swap');
        @keyframes shimmer {
          0% { background-position: -600px 0 }
          100% { background-position: 600px 0 }
        }
        @keyframes scanline {
          0% { transform: translateY(-100%) }
          100% { transform: translateY(100vh) }
        }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        .skel {
          background: linear-gradient(90deg, rgba(255,0,80,0.06) 25%, rgba(255,0,80,0.15) 50%, rgba(255,0,80,0.06) 75%);
          background-size: 600px 100%;
          animation: shimmer 1.6s infinite linear;
          border-radius: 2px;
        }
        .scan-line {
          position: fixed;
          left: 0; right: 0;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(255,0,80,0.4), transparent);
          animation: scanline 3s linear infinite;
          pointer-events: none;
          z-index: 10;
        }
        .grid-bg{position:fixed;inset:0;background-image:linear-gradient(rgba(255,0,80,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(255,0,80,0.02) 1px,transparent 1px);background-size:28px 28px;pointer-events:none;z-index:0}
      `}</style>

      <div className="scan-line" />
      <div className="grid-bg" />

      <div style={{ maxWidth: 900, margin: '0 auto', position: 'relative', zIndex: 2 }}>

        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,0,80,0.15)', paddingBottom: 16, marginBottom: 32 }}>
          <div className="skel" style={{ width: 180, height: 14 }} />
          <div className="skel" style={{ width: 80, height: 14 }} />
        </div>

        {/* Boot text lines */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 10, color: 'rgba(255,0,80,0.5)', letterSpacing: 2, marginBottom: 12 }}>&gt; INITIALIZING_SYSTEM...</div>
          <div className="skel" style={{ width: '60%', height: 10, marginBottom: 10 }} />
          <div className="skel" style={{ width: '45%', height: 10, marginBottom: 10 }} />
          <div className="skel" style={{ width: '52%', height: 10 }} />
        </div>

        {/* Big hero title skeleton */}
        <div className="skel" style={{ width: '75%', height: 64, marginBottom: 12 }} />
        <div className="skel" style={{ width: '40%', height: 16, marginBottom: 40 }} />

        {/* Stat cards row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 40, flexWrap: 'wrap' }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ flex: '1 1 180px', border: '1px solid rgba(255,0,80,0.12)', padding: 20 }}>
              <div className="skel" style={{ width: '50%', height: 10, marginBottom: 14 }} />
              <div className="skel" style={{ width: '70%', height: 28, marginBottom: 8 }} />
              <div className="skel" style={{ width: '40%', height: 10 }} />
            </div>
          ))}
        </div>

        {/* List rows */}
        <div style={{ borderTop: '1px solid rgba(255,0,80,0.1)', paddingTop: 24 }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid rgba(255,0,80,0.06)' }}>
              <div>
                <div className="skel" style={{ width: 120 + i * 30, height: 12, marginBottom: 8 }} />
                <div className="skel" style={{ width: 80, height: 9 }} />
              </div>
              <div className="skel" style={{ width: 60, height: 24 }} />
            </div>
          ))}
        </div>

        {/* Bottom cursor blink */}
        <div style={{ marginTop: 24, fontSize: 11, color: 'rgba(255,0,80,0.4)', letterSpacing: 2 }}>
          &gt; LOADING<span style={{ animation: 'blink 1s infinite' }}>_</span>
        </div>
      </div>
    </div>
  )

  return (
    <BrowserRouter>
      <CursorReticle />
      <Routes>
        <Route path="/" element={<Landing session={session} />} />
        <Route path="/login" element={!session ? <Login /> : <Navigate to="/dashboard" />} />
        <Route path="/signup" element={!session ? <Signup /> : <Navigate to="/dashboard" />} />
        <Route path="/dashboard" element={session ? <Dashboard session={session} /> : <Navigate to="/login" />} />
        <Route path="/app/riddle" element={
          <SubscribedRoute session={session}>
            <DailyRiddle session={session} />
          </SubscribedRoute>
        } />
        <Route path="/app/roast" element={
          <SubscribedRoute session={session}>
            <AIRoast session={session} />
          </SubscribedRoute>
        } />
        <Route path="/app/dark-history" element={
          <SubscribedRoute session={session}>
            <DarkHistory session={session} />
          </SubscribedRoute>
        } />
        <Route path="/app/villain" element={
          <SubscribedRoute session={session}>
            <VillainOrigin session={session} />
          </SubscribedRoute>
        } />
        <Route path="/app/rogues" element={
          <SubscribedRoute session={session}>
            <RoguesGallery session={session} />
          </SubscribedRoute>
        } />
      </Routes>
    </BrowserRouter>
  )
}

export default App
